import { photoUrl } from '@reditelji/shared';
import { useEffect, useRef } from 'react';
import './photoLightbox.css';

/**
 * Full-screen view of one post photo, shared by the volunteer briefing and the
 * Mesta editor. A coordinator checking a photo they just uploaded should be
 * looking at exactly what the marshal will see — one implementation is what
 * makes that true.
 *
 * Copy comes in from the caller: the briefing is bilingual and takes its
 * strings from `briefing/copy.ts`, while the coordinator screens carry their
 * Slovenian inline.
 */

export interface LightboxPhoto {
  key: string;
  /** Shown under the photo. Empty renders no caption row. */
  caption: string;
  /** Alt text, and the dialog's accessible name. */
  alt: string;
}

export interface LightboxLabels {
  close: string;
  prev: string;
  next: string;
  /** Position within the set, with `{n}` and `{m}` placeholders. */
  counter: string;
}

/**
 * Where a step lands. Clamps at both ends — and clamps the starting point too,
 * because the photo set can shrink under an open lightbox when a coordinator
 * deletes the picture someone is looking at.
 */
export function clampPhotoIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return Math.max(0, Math.min(count - 1, index));
}

export function PhotoLightbox({
  photos,
  index,
  labels,
  onIndex,
  onClose,
}: {
  photos: LightboxPhoto[];
  index: number;
  labels: LightboxLabels;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const swipeFrom = useRef<number | null>(null);

  // The page behind must not scroll under the overlay, and focus belongs
  // inside it — a briefing is read one-handed, often with the screen reader on.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);

  if (photos.length === 0) return null;
  const at = clampPhotoIndex(index, photos.length);
  const photo = photos[at]!;
  const step = (by: number) => onIndex(clampPhotoIndex(at + by, photos.length));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return onClose();
    if (e.key === 'ArrowLeft') return step(-1);
    if (e.key === 'ArrowRight') return step(1);
    if (e.key !== 'Tab') return;
    // Three controls at most, so the trap is a plain wrap-around.
    const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button') ?? [])];
    if (focusable.length === 0) return;
    const from = focusable.indexOf(document.activeElement as HTMLElement);
    const next = e.shiftKey ? from - 1 : from + 1;
    if (next >= 0 && next < focusable.length) return;
    e.preventDefault();
    focusable[e.shiftKey ? focusable.length - 1 : 0]!.focus();
  };

  return (
    <div
      ref={dialogRef}
      className="rr-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onClick={onClose}
      onTouchStart={(e) => (swipeFrom.current = e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        const from = swipeFrom.current;
        const to = e.changedTouches[0]?.clientX;
        swipeFrom.current = null;
        if (from === null || to === undefined || Math.abs(to - from) < 40) return;
        step(to < from ? 1 : -1);
      }}
    >
      <button className="rr-lightbox-x" aria-label={labels.close} onClick={onClose}>
        ✕
      </button>

      {/* Stop the backdrop's close handler at the photo itself. */}
      <figure className="rr-lightbox-fig" onClick={(e) => e.stopPropagation()}>
        <img src={photoUrl(photo.key, 'full')} alt={photo.alt} />
        <figcaption>
          {photo.caption ? <span>{photo.caption}</span> : null}
          {photos.length > 1 ? (
            <span className="rr-lightbox-count">
              {labels.counter.replace('{n}', String(at + 1)).replace('{m}', String(photos.length))}
            </span>
          ) : null}
        </figcaption>
      </figure>

      {photos.length > 1 ? (
        <div className="rr-lightbox-nav" onClick={(e) => e.stopPropagation()}>
          <button aria-label={labels.prev} disabled={at === 0} onClick={() => step(-1)}>
            ‹
          </button>
          <button
            aria-label={labels.next}
            disabled={at === photos.length - 1}
            onClick={() => step(1)}
          >
            ›
          </button>
        </div>
      ) : null}
    </div>
  );
}
